import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/site/Navbar';
import { Footer } from '../components/site/FooterCTA';

const LAST_UPDATED = '20 February 2026';

const Section = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="font-serif text-[26px] leading-tight" style={{ color: 'var(--yb-ink)' }}>
      {title}
    </h2>
    <div className="mt-3 space-y-3 text-[15px] leading-relaxed" style={{ color: 'var(--yb-text)' }}>
      {children}
    </div>
  </section>
);

const Terms = () => {
  useEffect(() => {
    document.title = 'Terms of Service · YouBuild';
  }, []);
  return (
    <div style={{ backgroundColor: 'var(--yb-bg)' }}>
      <Navbar />
      <main className="pt-28 pb-24" data-testid="terms-page">
        <div className="max-w-[760px] mx-auto px-6">
          <p className="text-[12.5px] uppercase tracking-widest" style={{ color: 'var(--yb-muted)' }}>
            Legal
          </p>
          <h1 className="font-serif text-[42px] sm:text-[54px] leading-[1.05] tracking-[-0.02em] mt-3" style={{ color: 'var(--yb-ink)' }}>
            Terms of Service
          </h1>
          <p className="mt-3 text-[13px]" style={{ color: 'var(--yb-muted)' }}>
            Last updated: {LAST_UPDATED}
          </p>

          <Section title="The short version">
            <p>
              YouBuild lets you describe a business in plain English and publish a working
              website. By creating an account you agree to these terms. Be honest about your
              business, don't publish anything illegal, and don't try to break the service. In
              return we'll do our best to keep YouBuild working well for you.
            </p>
          </Section>

          <Section title="Your account">
            <p>
              You must be at least 18 years old, or have permission from a parent or guardian, to
              create an account. Keep your login credentials safe. Anything done through your
              account is your responsibility.
            </p>
          </Section>

          <Section title="Your content">
            <p>
              You keep ownership of the business information, prompts, photos, and any other
              content you put into YouBuild. You grant us permission to store, process, and
              display that content so we can run the service for you, generate your website,
              deliver your published pages to visitors, and route quote-form submissions to you.
            </p>
            <p>
              You are responsible for making sure your content is accurate, does not infringe
              anyone else's rights, and complies with Australian law and advertising rules,
              including consumer guarantees and any professional licensing requirements that
              apply to your trade.
            </p>
          </Section>

          <Section title="What you can't do">
            <p>
              Don't use YouBuild to publish anything unlawful, deceptive, or that promotes
              violence, harassment, or discrimination. Don't misrepresent someone else's business
              as your own, and don't scrape, reverse-engineer, or interfere with the service.
            </p>
          </Section>

          <Section title="Plans and billing">
            <p>
              Some features (including publishing to a live URL) require a paid plan. Prices,
              trial length, and included quotas are shown on the pricing page and can change with
              notice. Subscriptions are processed by Stripe. You can cancel at any time; access
              continues until the end of the current billing period.
            </p>
          </Section>

          <Section title="AI generation">
            <p>
              YouBuild uses AI to draft your site. AI can make mistakes: it may misspell a suburb,
              invent a service you don't actually offer, or produce content that needs a human
              review before it goes live. You are responsible for reviewing every page you
              publish. YouBuild does not guarantee that generated pages are error-free, complete,
              or suitable for any particular purpose.
            </p>
          </Section>

          <Section title="Availability">
            <p>
              We work hard to keep YouBuild up, but we do not guarantee uninterrupted service. We
              may pause, change, or discontinue features from time to time, particularly during
              maintenance.
            </p>
          </Section>

          <Section title="Termination">
            <p>
              You can delete your account at any time from the app or by emailing us. We can
              suspend or terminate accounts that breach these terms or that harm other users.
            </p>
          </Section>

          <Section title="Liability">
            <p>
              To the extent permitted by law, YouBuild is provided "as is" and we exclude implied
              warranties. Our total liability for any claim relating to the service is limited to
              the amount you paid us in the twelve months before the claim. Nothing in these
              terms limits any consumer guarantees you have under the Australian Consumer Law
              that cannot be excluded.
            </p>
          </Section>

          <Section title="Governing law">
            <p>
              These terms are governed by the laws of Australia. If a dispute cannot be resolved
              informally, it must be brought in the courts of Australia.
            </p>
          </Section>

          <Section title="Contact">
            <p>
              Questions about these terms: <a href="mailto:ginacastela62@gmail.com" style={{ color: 'var(--yb-indigo)' }}>ginacastela62@gmail.com</a>.
            </p>
            <p>
              <Link to="/" style={{ color: 'var(--yb-indigo)' }}>← Back to YouBuild</Link>
            </p>
          </Section>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Terms;
